import React from 'react';
import { motion } from 'framer-motion';
import { PlugZap, Cpu, Cog, Disc, ShieldAlert, CircuitBoard, Gauge, Wind, Navigation, Battery, Thermometer, Activity, BadgeCheck, Radio } from 'lucide-react';

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

const OC = {
  canvas: '#030712',
  base: '#0A0E17',
  ok: '#10b981',
  scan: '#22d3ee',
  pending: '#f59e0b',
};
const GRAIN = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

const Reticle = ({ c }) => (
  <>
    {['top-0 left-0 border-t-2 border-l-2', 'top-0 right-0 border-t-2 border-r-2', 'bottom-0 left-0 border-b-2 border-l-2', 'bottom-0 right-0 border-b-2 border-r-2'].map((p) => (
      <span key={p} className={`pointer-events-none absolute ${p} h-2.5 w-2.5`} style={{ borderColor: c }} />
    ))}
  </>
);

const MODULES = [
  { id: 'ecu', abbr: 'ECU', name: 'Engine Control', Icon: Cpu, reveal: 0.30 },
  { id: 'tcu', abbr: 'TCU', name: 'Transmission', Icon: Cog, reveal: 0.35 },
  { id: 'abs', abbr: 'ABS', name: 'Anti-lock Brakes', Icon: Disc, reveal: 0.40 },
  { id: 'srs', abbr: 'SRS', name: 'Airbag / SRS', Icon: ShieldAlert, reveal: 0.46 },
  { id: 'bcm', abbr: 'BCM', name: 'Body Control', Icon: CircuitBoard, reveal: 0.52 },
  { id: 'ipc', abbr: 'IPC', name: 'Instrument Cluster', Icon: Gauge, reveal: 0.58 },
  { id: 'hvac', abbr: 'HVAC', name: 'Climate Control', Icon: Wind, reveal: 0.64 },
  { id: 'eps', abbr: 'EPS', name: 'Power Steering', Icon: Navigation, reveal: 0.70 },
];

const PIDS = [
  { k: 'RPM', unit: 'rpm', Icon: Gauge, get: (t) => 780 + Math.round(Math.abs(Math.sin(t * 33)) * 90) },
  { k: 'Battery', unit: 'V', Icon: Battery, get: (t) => (14.1 + Math.sin(t * 20) * 0.12).toFixed(1) },
  { k: 'Coolant', unit: '°C', Icon: Thermometer, get: (t) => 86 + Math.round(Math.abs(Math.sin(t * 7)) * 4) },
  { k: 'MAF', unit: 'g/s', Icon: Wind, get: (t) => (3.0 + Math.abs(Math.sin(t * 25)) * 0.7).toFixed(1) },
  { k: 'Fuel trim', unit: '%', Icon: Activity, get: (t) => `${(Math.sin(t * 15) * 1.8).toFixed(1)}` },
];

const ModuleNode = ({ m, t, i, compact = false }) => {
  const on = t >= m.reveal;
  const { Icon } = m;
  const col = on ? OC.ok : OC.scan;
  if (compact) {
    return (
      <motion.div
        data-testid={`obd-module-${m.id}-m`}
        initial={{ opacity: 0, x: 8 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.05 + i * 0.03, duration: 0.3 }}
        className="relative flex min-h-0 items-center gap-2 overflow-hidden rounded-sm px-2"
        style={{ background: OC.base, border: `1px solid ${on ? 'rgba(16,185,129,0.5)' : 'rgba(34,211,238,0.3)'}` }}
      >
        <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: col }} />
        <span className="shrink-0 font-mono text-[11px] font-black tracking-wider text-white">{m.abbr}</span>
        <span className="min-w-0 flex-1 truncate text-[8px] uppercase tracking-[0.1em] text-slate-400">{m.name}</span>
        <span className="shrink-0 inline-flex items-center gap-1 text-[8px] font-bold uppercase tracking-wider" style={{ color: col }}>
          {on ? <><BadgeCheck className="h-2.5 w-2.5" /> 0 DTC</> : <><span className="h-1 w-1 rounded-full animate-pulse" style={{ background: col }} /> scan</>}
        </span>
      </motion.div>
    );
  }
  return (
    <motion.div
      data-testid={`obd-module-${m.id}`}
      initial={{ opacity: 0, x: 8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.05 + i * 0.03, duration: 0.3 }}
      className="relative flex items-center gap-2 overflow-hidden rounded-sm px-2.5 py-2"
      style={{ background: OC.base, border: `1px solid ${on ? 'rgba(16,185,129,0.5)' : 'rgba(34,211,238,0.3)'}`, boxShadow: on ? `inset 0 0 18px rgba(16,185,129,0.07)` : 'none' }}
    >
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-sm" style={{ background: `${col}1f`, color: col }}>
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-1.5">
          <span className="font-mono text-xs font-black tracking-wider text-white">{m.abbr}</span>
          <span className="truncate text-[9px] uppercase tracking-[0.1em] text-slate-400">{m.name}</span>
        </div>
        <div className="flex items-center gap-1 text-[8px] font-bold uppercase tracking-wider" style={{ color: col }}>
          {on ? <><BadgeCheck className="h-2.5 w-2.5" /> Responding · 0 DTC</> : <><span className="h-1 w-1 rounded-full animate-pulse" style={{ background: col }} /> Interrogating…</>}
        </div>
      </div>
    </motion.div>
  );
};

const PidChip = ({ p, t, compact = false }) => {
  const { Icon } = p;
  return (
    <div className="relative flex items-center gap-1.5 overflow-hidden rounded-sm px-2 py-1.5" style={{ background: 'rgba(10,14,23,0.92)', border: '1px solid rgba(34,211,238,0.28)' }}>
      <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: OC.scan }} />
      <div className="leading-none">
        <div className="text-[7px] font-bold uppercase tracking-[0.14em] text-slate-400">{p.k}</div>
        <div className="font-mono text-[11px] sm:text-[13px] font-black tabular-nums text-white" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}>
          {p.get(t)}<span className="ml-0.5 text-[8px] text-slate-400">{p.unit}</span>
        </div>
      </div>
      {!compact && (
        <span className="ml-1 flex items-end gap-[2px] h-4">
          {[0.7, 1.0, 0.55].map((d, i) => (
            <motion.span key={i} className="w-[2px] rounded-full" style={{ background: OC.scan, boxShadow: `0 0 4px ${OC.scan}` }} initial={{ height: 4 }} animate={{ height: [4, 13, 6, 11, 4] }} transition={{ duration: d + 0.5, repeat: Infinity, ease: 'easeInOut', delay: i * 0.14 }} />
          ))}
        </span>
      )}
    </div>
  );
};

const PortLock = ({ t, compact = false }) => {
  const linked = t >= 0.16;
  const col = linked ? OC.ok : OC.scan;
  return (
    <div className="relative" style={{ width: compact ? 128 : 170, height: compact ? 78 : 118 }}>
      <div className="absolute inset-0 rounded-sm" style={{ border: `2px solid ${col}`, boxShadow: `0 0 22px -4px ${col}` }} />
      <Reticle c={col} />
      {linked && [0, 1, 2].map((i) => (
        <motion.span key={i} className="pointer-events-none absolute left-1/2 top-1/2 rounded-full border" style={{ width: 28, height: 28, marginLeft: -14, marginTop: -14, borderColor: col }} initial={{ opacity: 0.5, scale: 0.5 }} animate={{ opacity: 0, scale: compact ? 2.2 : 3.2 }} transition={{ duration: 1.8, repeat: Infinity, delay: i * 0.6, ease: 'easeOut' }} />
      ))}
      {/* crosshair */}
      <span className="absolute left-1/2 top-1/2 h-4 w-px -translate-x-1/2 -translate-y-1/2" style={{ background: `${col}aa` }} />
      <span className="absolute left-1/2 top-1/2 h-px w-4 -translate-x-1/2 -translate-y-1/2" style={{ background: `${col}aa` }} />
      {compact ? (
        <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1 py-0.5 font-mono text-[8px] font-black uppercase tracking-[0.12em]" style={{ background: 'rgba(3,7,18,0.78)', color: col }}>
          <PlugZap className="h-2.5 w-2.5" /> OBD-II · {linked ? 'Linked' : 'Sync…'}
        </div>
      ) : (
        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 translate-y-full whitespace-nowrap rounded-sm px-2 py-1 font-mono text-[9px] font-black uppercase tracking-[0.16em]" style={{ background: OC.base, border: `1px solid ${col}66`, color: col }}>
          <span className="inline-flex items-center gap-1"><PlugZap className="h-3 w-3" /> OBD-II Port · {linked ? 'Linked' : 'Handshake…'}</span>
        </div>
      )}
    </div>
  );
};

const ModulePanel = ({ t, compact = false }) => {
  const responded = MODULES.filter((m) => t >= m.reveal).length;
  const scanY = clamp((t - 0.22) / 0.5, 0, 1);
  const dtcDone = t >= 0.78;
  return (
    <div className={`relative flex w-full min-w-0 min-h-0 flex-col overflow-hidden rounded-sm ${compact ? 'p-2' : 'p-3'}`} style={{ background: 'rgba(10,14,23,0.95)', border: '1px solid rgba(16,185,129,0.28)', boxShadow: '0 0 40px -14px rgba(16,185,129,0.35)' }}>
      <Reticle c="rgba(16,185,129,0.5)" />
      {/* bus scan line */}
      {t < 0.74 && <div className="pointer-events-none absolute inset-x-0 z-10" style={{ top: `${scanY * 100}%`, height: 2, background: `linear-gradient(90deg, transparent, ${OC.scan}, transparent)`, boxShadow: `0 0 14px 1px ${OC.scan}` }} />}
      <div className="relative mb-1.5 flex shrink-0 items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-[9px] sm:text-[10px] font-black uppercase tracking-[0.16em] text-emerald-300"><CircuitBoard className="h-3.5 w-3.5" /> CAN Bus · Modules</span>
        <span className="font-mono text-[9px] sm:text-[10px] font-black tabular-nums" style={{ color: OC.ok }}>{String(responded).padStart(2, '0')}/{String(MODULES.length).padStart(2, '0')}</span>
      </div>
      <div className="relative mb-1.5 h-1 shrink-0 overflow-hidden rounded-full" style={{ background: 'rgba(34,211,238,0.12)' }}>
        <motion.div className="absolute top-0 h-full w-10 -translate-x-1/2 rounded-full" style={{ background: `linear-gradient(90deg, transparent, ${OC.scan}, transparent)` }} animate={{ left: ['0%', '100%'] }} transition={{ duration: 1.1, repeat: Infinity, ease: 'linear' }} />
      </div>
      <div className={`relative grid min-h-0 flex-1 auto-rows-fr ${compact ? 'grid-cols-1 grid-rows-8 gap-1' : 'grid-cols-2 grid-rows-4 gap-2'}`}>
        {MODULES.map((m, i) => <ModuleNode key={m.id} m={m} t={t} i={i} compact={compact} />)}
      </div>
      {/* DTC result */}
      <div className="relative mt-1.5 flex shrink-0 items-center justify-between rounded-sm px-2.5 py-1.5" style={{ background: OC.base, border: `1px solid ${dtcDone ? 'rgba(16,185,129,0.5)' : 'rgba(245,158,11,0.4)'}` }}>
        <span className="text-[8px] sm:text-[9px] font-bold uppercase tracking-[0.14em]" style={{ color: dtcDone ? OC.ok : OC.pending }}>{dtcDone ? 'Fault codes' : 'Scanning DTC…'}</span>
        <span className="font-mono text-[9px] sm:text-[11px] font-black tabular-nums" style={{ color: dtcDone ? OC.ok : OC.pending }}>{dtcDone ? '0 stored · 0 pending' : '· · ·'}</span>
      </div>
    </div>
  );
};

const Seal = ({ t, mobile = false }) => {
  const on = t >= 0.82;
  return (
    <motion.div
      data-testid={`obd-seal${mobile ? '-m' : ''}`}
      className="relative flex shrink-0 items-center justify-center gap-2 sm:gap-3 overflow-hidden rounded-sm px-3 sm:px-5 py-2 sm:py-2.5"
      initial={{ opacity: 0, scale: 1 }}
      animate={on ? { opacity: 1, scale: [1.3, 1] } : { opacity: 0, scale: 1 }}
      transition={on ? { duration: 0.5, ease: [0.16, 1, 0.3, 1] } : { duration: 0.3 }}
      style={{ background: OC.base, border: '1px solid rgba(16,185,129,0.6)', boxShadow: `0 0 34px -8px ${OC.ok}` }}
    >
      <Reticle c={OC.ok} />
      <BadgeCheck className="h-5 w-5 sm:h-6 sm:w-6 shrink-0" style={{ color: OC.ok, filter: `drop-shadow(0 0 8px ${OC.ok})` }} />
      <span className="font-mono text-[11px] sm:text-lg font-black uppercase tracking-[0.12em] sm:tracking-[0.18em] whitespace-nowrap" style={{ color: OC.ok, textShadow: `0 0 18px ${OC.ok}aa` }}>8 ECUs · 0 Fault Codes</span>
      <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-widest text-slate-400">· all electronics healthy</span>
    </motion.div>
  );
};

const OBDBeat = ({ t = 0 }) => {
  const linked = t >= 0.16;
  return (
    <motion.div data-testid="obd-beat" className="absolute inset-0 z-40 overflow-hidden" style={{ background: OC.canvas }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
      {/* hero footage: the OBD-II port with the scanner plugged in */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <img src="/inspection/obd.jpg" alt="OBD-II diagnostic port with scanner plugged in" className="h-full w-full object-cover" draggable={false} style={{ transform: `scale(${1.06 + 0.08 * t}) translate3d(${-6 * t}px, ${-4 * t}px, 0)`, transformOrigin: '38% 55%', transition: 'transform 0.12s linear' }} />
      </div>
      {/* scrims for legibility */}
      <div className="pointer-events-none absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(3,7,18,0.35) 0%, rgba(3,7,18,0.15) 30%, rgba(3,7,18,0.78) 68%, rgba(3,7,18,0.92) 100%)' }} />
      <div className="pointer-events-none absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(3,7,18,0.85), rgba(3,7,18,0) 45%)' }} />
      <div className="pointer-events-none absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(3,7,18,0.7), rgba(3,7,18,0) 30%)' }} />
      {/* engineering grid + trust ramp */}
      <div className="pointer-events-none absolute inset-0 opacity-60" style={{ backgroundImage: 'linear-gradient(rgba(34,211,238,0.05) 1px,transparent 1px),linear-gradient(90deg,rgba(34,211,238,0.05) 1px,transparent 1px)', backgroundSize: '40px 40px' }} />
      {/* crt + grain */}
      <div className="pointer-events-none absolute inset-0 z-10 opacity-[0.05]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)', backgroundSize: '100% 4px' }} />
      <div className="pointer-events-none absolute z-10 mix-blend-overlay opacity-[0.05] animate-[grainShift_1.1s_steps(3)_infinite]" style={{ inset: '8px', backgroundImage: GRAIN, backgroundSize: '160px 160px' }} />

      <div className="relative z-20 flex h-full w-full flex-col p-3 sm:p-6">
        {/* header */}
        <div className="flex shrink-0 items-center justify-between gap-2">
          <div className="inline-flex min-w-0 items-center gap-2">
            <span className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-sm border border-cyan-400/40 px-2 py-1 text-[9px] sm:text-[10px] font-black uppercase tracking-[0.14em] sm:tracking-[0.16em] text-cyan-300" style={{ background: OC.base }}><PlugZap className="h-3.5 w-3.5" /><span className="sm:hidden">OBD-II</span><span className="hidden sm:inline">OBD-II Diagnostic</span></span>
            <span className="hidden md:inline whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.16em] text-slate-400">ISO 15765-4 CAN · 500 kbps</span>
          </div>
          <div className="inline-flex shrink-0 items-center gap-2">
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap font-mono text-[9px] sm:text-[10px] uppercase tracking-wider" style={{ color: linked ? OC.ok : OC.scan }}>
              <Radio className={`h-3.5 w-3.5 ${linked ? '' : 'animate-pulse'}`} /><span className="sm:hidden">{linked ? 'Linked' : 'Sync…'}</span><span className="hidden sm:inline">{linked ? 'Scan tool linked' : 'Handshake…'}</span>
            </span>
          </div>
        </div>

        {/* body — desktop */}
        <div className="hidden sm:flex flex-1 min-h-0 mt-4 flex-col gap-4">
          <div className="grid flex-1 min-h-0 grid-cols-12 gap-5">
            {/* left: footage stays visible — port lock + live telemetry */}
            <div className="col-span-7 relative min-h-0">
              <div className="absolute" style={{ left: '58%', top: '30%' }}><PortLock t={t} /></div>
              <div className="absolute inset-x-0 bottom-0 grid grid-cols-5 gap-2">
                {PIDS.map((p) => <PidChip key={p.k} p={p} t={t} />)}
              </div>
            </div>
            {/* right: control-module scan */}
            <div className="col-span-5 flex min-h-0"><ModulePanel t={t} /></div>
          </div>
          <Seal t={t} />
        </div>

        {/* body — mobile (portrait 3:4) */}
        <div className="sm:hidden flex flex-1 min-h-0 mt-2 flex-col gap-2">
          {/* hero footage window — the OBD-II port with the scanner plugged in */}
          <div className="relative shrink-0 overflow-hidden rounded-sm" style={{ height: 110, border: '1px solid rgba(34,211,238,0.3)' }}>
            <img src="/inspection/obd.jpg" alt="OBD-II port with scanner plugged in" className="absolute inset-0 h-full w-full object-cover" draggable={false} style={{ objectPosition: '44% 52%', transform: `scale(${1.05 + 0.06 * t})`, transition: 'transform 0.12s linear' }} />
            <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(65% 75% at 48% 52%, rgba(3,7,18,0) 42%, rgba(3,7,18,0.55) 100%)' }} />
            <div className="absolute" style={{ left: '50%', top: '50%', transform: 'translate(-50%,-50%)' }}><PortLock t={t} compact /></div>
            <span className="absolute left-2 top-2 rounded-sm px-1.5 py-0.5 font-mono text-[7px] font-bold uppercase tracking-wider text-cyan-300" style={{ background: 'rgba(3,7,18,0.72)' }}>ISO 15765 CAN</span>
          </div>
          <div className="grid shrink-0 grid-cols-3 gap-1.5">
            {PIDS.slice(0, 3).map((p) => <PidChip key={p.k} p={p} t={t} compact />)}
          </div>
          <div className="flex min-h-0 flex-1"><ModulePanel t={t} compact /></div>
          <Seal t={t} mobile />
        </div>
      </div>
    </motion.div>
  );
};

export default OBDBeat;
